/**
 * PDF.js configuration - exports a loader function that fetches PDF.js from CDN
 * 
 * CR086: Uses CDN loading to avoid Vite bundler issues with pdfjs-dist.
 * The dynamic import from node_modules fails intermittently in dev/preview.
 * Instead, we load the ES module directly from cdnjs.
 */

// CDN URLs for PDF.js 4.0.379 (matching our installed version)
const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.min.mjs';
const PDFJS_WORKER_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/pdf.worker.min.mjs';

// Cache the loaded module
let pdfjsModule: typeof import('pdfjs-dist') | null = null;
let loadPromise: Promise<typeof import('pdfjs-dist')> | null = null;

/**
 * Loads PDF.js from CDN and configures the worker.
 * Returns a cached instance on subsequent calls.
 */
export async function getPdfjs(): Promise<typeof import('pdfjs-dist')> {
  // Return cached module if available
  if (pdfjsModule) {
    return pdfjsModule;
  }
  
  // Return existing load promise to prevent duplicate loads
  if (loadPromise) {
    return loadPromise;
  }
  
  loadPromise = (async () => {
    try {
      console.log('[pdfjs-config] Loading PDF.js from CDN...');
      
      // Import PDF.js ES module from CDN
      const pdfjs = await import(/* @vite-ignore */ PDFJS_CDN) as typeof import('pdfjs-dist');
      
      // Configure worker to use CDN
      pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
      
      pdfjsModule = pdfjs;
      console.log('[pdfjs-config] PDF.js loaded successfully from CDN');
      return pdfjs;
    } catch (cdnError) {
      console.warn('[pdfjs-config] CDN load failed, trying node_modules fallback...', cdnError);
      
      // Fallback to node_modules (may work in some environments)
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
        pdfjsModule = pdfjs;
        console.log('[pdfjs-config] PDF.js loaded from node_modules fallback');
        return pdfjs;
      } catch (fallbackError) {
        console.error('[pdfjs-config] All PDF.js load methods failed:', fallbackError);
        throw new Error('Failed to load PDF.js library. Please refresh the page and try again.');
      }
    }
  })();
  
  return loadPromise;
}

/**
 * Resets the cached module (useful for testing or recovery)
 */
export function resetPdfjsCache(): void {
  pdfjsModule = null;
  loadPromise = null;
}
